import Skeleton from '@material-ui/lab/Skeleton';
import moment, { Moment } from 'moment';
import React from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  ICalendarSetting,
  ReportTypeID,
  reportTypeIDToDescriptionTKey,
} from '~/src/dash/apiV2/reporting/reportTypes';
import { CalendarV2 } from '~/src/dash/components/calendar/CalendarV2';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { getSimpleSingleOptionSelects } from '~/src/dash/components/common';
import { SimpleSingleSelect } from '~/src/dash/components/dropdowns/SimpleSingleSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import { getLineHeight } from '~/src/dash/components/text/common';
import {
  Col,
  Cols,
  ISimpleOption,
  Row,
  Rows,
  spacingV2,
  Text,
  TKey,
  TooltipWithTranslation,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  DateRange,
  DateRangeInt,
  DateRangeToDateRangeInt,
  defaultDateRanges,
  findDateRange,
  IDateRange,
} from '~/src/dash/utils/dateTimeRanges';
import { getTimezone } from '~/src/dash/utils/timezone';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { generateCustomDateRange } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';
import { EReportTypeCategory } from '~/src/dash/views/reporting/common';
import { useReportBuilderStore } from '~/src/dash/views/reporting/ReportBuilder/context';
import { QuickReportSelector } from '~/src/dash/views/reporting/ReportBuilder/Query/QuickReportSelector';

interface IPillProps {
  isSelected: boolean;
}

const QuickReportsPill = ({ isSelected }: IPillProps) => {
  return (
    <Col style={{ display: 'flex', alignItems: 'center' }}>
      <IconV2
        name='flash'
        color={isSelected ? publicaColors.turquoise : publicaColors.darkGray}
        style={{ marginRight: spacingV2.extraSmall }}
      />
      <Text
        tKey='ReportingQuickReports'
        small
        bold={isSelected}
        color={isSelected ? publicaColors.turquoise : publicaColors.darkGray}
      />
    </Col>
  );
};

const InvestigationPill = ({ isSelected }: IPillProps) => {
  return (
    <Col style={{ display: 'flex', alignItems: 'center' }}>
      <IconV2
        name='compass'
        color={isSelected ? publicaColors.turquoise : publicaColors.darkGray}
        style={{ marginRight: spacingV2.extraSmall }}
      />
      <Text
        tKey='ReportingInvestigation'
        small
        bold={isSelected}
        color={isSelected ? publicaColors.turquoise : publicaColors.darkGray}
      />
    </Col>
  );
};

interface IQuerySectionProps {
  reportTypes: IReportTypeListItem[];
  reportTypeCategory: EReportTypeCategory;
  setSelectedReportTypeID: (id: number) => void;
  setStartDate: (startDate: moment.Moment) => void;
  setEndDate: (startDate: moment.Moment) => void;
  setDateRange: (num: DateRangeInt) => void;
  isLoading: boolean;
  showCalendar?: boolean;
  isCalendarDisabled?: boolean;
  onError: (err: any, msg: string) => void;
  calendarSetting?: ICalendarSetting;
  isIasReport?: boolean;
}

export interface IReportTypeListItem extends ISimpleOption {
  category: number;
  description: string;
}

export const QuerySection = ({
  reportTypes,
  reportTypeCategory,
  setSelectedReportTypeID,
  setStartDate,
  setDateRange,
  setEndDate,
  isLoading,
  showCalendar,
  isCalendarDisabled,
  onError,
  calendarSetting = { MaxDaysLookBack: -1, MaxDaysPerQuery: -1, MinDaysLookBack: -1 },
  isIasReport,
}: IQuerySectionProps) => {
  const { t } = usePublicaTranslation();
  const { user, session, isSuperUser } = useBaseStore();
  const { endDate, reportTypeID, startDate } = useReportBuilderStore();
  const timezone = getTimezone(user.user.Timezone, session.publisher.Timezone);
  const selectedReportType = reportTypes.find(
    (reportType) => reportType.value === String(reportTypeID),
  );
  const quickReports = reportTypes
    .filter((reportType) => reportType.category === 1)
    .map((reportType) => {
      return {
        ...reportType,
        isSelected: reportType.value === String(reportTypeID),
      };
    });
  const investigationReports = reportTypes.filter((reportType) => reportType.category === 2);

  const reportTypeCategories: Array<ISimpleOption> = [
    {
      key: 'reportTypeCategory',
      value: EReportTypeCategory.QuickReports,
      label: t('ReportingQuickReports'),
      customComponent: QuickReportsPill({
        isSelected: reportTypeCategory === EReportTypeCategory.QuickReports,
      }),
    },
    {
      key: 'reportTypeCategory',
      value: EReportTypeCategory.InvestigationReports,
      label: t('ReportingInvestigation'),
      customComponent: InvestigationPill({
        isSelected: reportTypeCategory === EReportTypeCategory.InvestigationReports,
      }),
    },
  ];

  const getReportTypeDescription = (): TKey => {
    if (reportTypeCategory === EReportTypeCategory.InvestigationReports) {
      return 'ReportingInvestigationDescription';
    }
    return selectedReportType
      ? reportTypeIDToDescriptionTKey[selectedReportType?.value]
      : 'UnknownReport';
  };

  // when user updates the date range using the date toggle, update the query
  const onDateTimeRangeTogglesUpdate = (rangeKey: DateRange) => {
    const newDateRange = findDateRange(rangeKey);
    const { startDate, endDate } = newDateRange.generateQueryDates(timezone);

    if (
      calendarSetting.MaxDaysPerQuery > 0 &&
      endDate.diff(startDate, 'days') + 1 > calendarSetting.MaxDaysPerQuery
    ) {
      onError(
        t('OutOfRangeDays'),
        t('ReportingSnackbarTheDateRangeExceedsMaximum', {
          count: calendarSetting.MaxDaysPerQuery,
        }),
      );
      return;
    }
    unstable_batchedUpdates(() => {
      setStartDate(startDate);
      setEndDate(endDate);
      setDateRange(DateRangeToDateRangeInt[rangeKey]);
    });
  };

  // helper function to find if there is any matched preset value for the datetime toggle when
  // user updates time range using the calendar
  const findDateTimeRangeToggleValue = (
    startDate: moment.Moment,
    endDate: moment.Moment,
  ): DateRange => {
    if (!startDate || !endDate) {
      return defaultDateRanges[0].key as DateRange;
    }
    const dateRange = defaultDateRanges.find((range) =>
      range.isWithinRange(startDate.clone().startOf('day'), endDate.clone().endOf('day'), timezone),
    );
    return (
      (dateRange?.key as DateRange) || generateCustomDateRange(endDate.diff(startDate, 'days')).key
    );
  };

  // when user updates the date range using the calendar, update the query and the date toggle
  const onCalendarUpdate = ({ newStartDate, newEndDate }: INewCalDates) => {
    if (
      calendarSetting.MaxDaysPerQuery > 0 &&
      newEndDate.diff(newStartDate, 'days') + 1 > calendarSetting.MaxDaysPerQuery
    ) {
      onError(
        t('OutOfRangeDays'),
        t('ReportingSnackbarTheDateRangeExceedsMaximum', {
          count: calendarSetting.MaxDaysPerQuery,
        }),
      );
      return;
    }
    unstable_batchedUpdates(() => {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setDateRange(DateRangeToDateRangeInt[findDateTimeRangeToggleValue(newStartDate, newEndDate)]);
    });
  };

  const handleClickReportTypeCategoryPillSwitcher = (value: EReportTypeCategory) => {
    if (!isSuperUser) {
      track(
        'Report - Report Type Switcher',
        {
          is_super_user: isSuperUser,
          report_type: value,
        },
        user.user,
      );
    }
    if (value === EReportTypeCategory.QuickReports) {
      setSelectedReportTypeID(ReportTypeID.AuctionQuickReport);
    } else {
      setSelectedReportTypeID(ReportTypeID.AuctionReport);
    }
  };

  const onReportTypeChange = (id: string) => {
    setSelectedReportTypeID(+id);
  };

  const calcMinCalendarDate = (): Moment | undefined => {
    if (reportTypeCategory === EReportTypeCategory.InvestigationReports) {
      if (calendarSetting.MaxDaysLookBack > 0) {
        return moment()
          .tz(timezone)
          .subtract(calendarSetting.MaxDaysLookBack - 1, 'day');
      }
      return undefined;
    }
    return moment('03/01/2024', 'MM/DD/YYYY');
  };

  return (
    <Rows>
      <Row>
        <Cols gap={spacingV2.medium}>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <IconV2 name='layers' />
          </Col>
          <Col>
            <Text tKey='ReportingQuery' sizeV2 large bold />
          </Col>
        </Cols>
      </Row>

      {!isLoading ? (
        <Rows extraLargeHorizontalPaddingV2 noVerticalPaddingV2>
          {showCalendar && (
            <Row paddingTop='0px'>
              <Cols style={{ alignItems: 'flex-start' }}>
                <Col
                  width={120}
                  style={{ marginTop: spacingV2.small, paddingTop: spacingV2.extraSmall }}
                >
                  <Text tKey='Period' small bold />
                </Col>
                <Col width={`calc(100% - 120px)`}>
                  <Cols wrap style={{ alignItems: 'center' }}>
                    <Col paddingRight={spacingV2.medium} style={{ marginTop: spacingV2.small }}>
                      <CalendarV2
                        calStartDate={startDate}
                        calEndDate={endDate}
                        handleApplyDatesChange={onCalendarUpdate}
                        uniqueID='reportFormCal'
                        isDisabled={isCalendarDisabled}
                        maxDaysRange={
                          calendarSetting.MaxDaysPerQuery > 0
                            ? calendarSetting.MaxDaysPerQuery
                            : undefined
                        }
                        minDate={calcMinCalendarDate()}
                      />
                    </Col>
                    <Col style={{ marginTop: spacingV2.small }}>
                      <Cols style={{ alignItems: 'center' }}>
                        <Col style={{ marginRight: spacingV2.small }}>
                          <DateTimeRangeToggles
                            isDisabled={isCalendarDisabled}
                            ranges={defaultDateRanges}
                            activeKey={findDateTimeRangeToggleValue(startDate, endDate)}
                            onClick={onDateTimeRangeTogglesUpdate}
                            checkIsRangeDisabled={(range: IDateRange) => {
                              if (isIasReport && range.key === 'Today') return true;
                              if (calendarSetting.MaxDaysPerQuery <= 0) return false;
                              const dateRange = defaultDateRanges
                                .find((dateRange) => dateRange.key === range.key)
                                ?.getDateRange(timezone);
                              return !!(dateRange && dateRange > calendarSetting.MaxDaysPerQuery);
                            }}
                          />
                        </Col>
                        <Col>
                          <TooltipWithTranslation tKey='ReportingTooltipTimeRange'>
                            <div>
                              <IconV2
                                name='info-static'
                                color={publicaColors.teal}
                                style={{ marginTop: spacingV2.extraSmall }}
                              />
                            </div>
                          </TooltipWithTranslation>
                        </Col>
                      </Cols>
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
          )}

          <Row noVerticalPaddingV2 marginBottom={spacingV2.extraSmall}>
            <Cols style={{ alignItems: 'center' }}>
              <Col width={120}>
                <Text tKey='ReportType' small bold />
              </Col>
              <Col displayFlex>
                <PillSwitcherV2
                  size='baseLong'
                  options={getSimpleSingleOptionSelects(reportTypeCategories, reportTypeCategory)}
                  onChange={(value: EReportTypeCategory) =>
                    handleClickReportTypeCategoryPillSwitcher(value)
                  }
                />
              </Col>
            </Cols>
          </Row>

          <Row smallVerticalPaddingV2>
            <Cols style={{ alignItems: 'center' }}>
              <Col width={120}>{''}</Col>
              <Col width={`calc(100% - 120px)`}>
                {reportTypeCategory === EReportTypeCategory.QuickReports &&
                  quickReports.length > 0 && (
                    <QuickReportSelector options={quickReports} onChange={onReportTypeChange} />
                  )}
                {reportTypeCategory === EReportTypeCategory.InvestigationReports && (
                  <SimpleSingleSelect
                    inputWidth='18rem'
                    options={getSimpleSingleOptionSelects(
                      investigationReports,
                      String(reportTypeID),
                    )}
                    isSearchEnabled
                    isSortEnabled
                    onChange={onReportTypeChange}
                    popoverWidth='17.5rem'
                  />
                )}
                <Col smallVerticalPaddingV2 style={{ maxWidth: '700px' }}>
                  <Cols style={{ alignItems: 'top' }}>
                    <Col>
                      <IconV2
                        name='activity'
                        small
                        color={publicaColors.purple}
                        style={{
                          backgroundColor: publicaColors.veryLightPurple,
                          borderRadius: '1rem',
                          marginRight: '.25rem',
                          position: 'relative',
                        }}
                      />
                    </Col>
                    <Col>
                      <Text tKey={getReportTypeDescription()} extraSmall inlineBlock />
                    </Col>
                  </Cols>
                </Col>
              </Col>
            </Cols>
          </Row>
        </Rows>
      ) : (
        <Skeleton
          variant='text'
          animation='wave'
          width='100%'
          height={getLineHeight('extraLarge')}
        />
      )}
    </Rows>
  );
};
